'use client';

import Typography, { type TypographyProps } from '@mui/material/Typography';
import { useEffect, useMemo, useState, type ElementType } from 'react';
import { animated, useInView, useTrail } from 'react-spring';
import { SPRING } from './rörelse-konfig';
import { useRörelse } from './RörelseKontext';

const AnimeradSpan = animated('span');

type Props = {
    text: string | string[];
    /** Dela på ord eller på rad. Aldrig på tecken — se noten nedan. */
    enhet?: 'ord' | 'rad';
    variant?: TypographyProps['variant'];
    component?: ElementType;
    /** Millisekunder innan spåret börjar. */
    fördröjning?: number;
    /** Hur långt enheterna reser, i pixlar. */
    avstånd?: number;
    engångs?: boolean;
    sx?: TypographyProps['sx'];
};

/**
 * Text som avtäcks enhet för enhet.
 *
 * Delas medvetet bara på ord eller rad. En rubrik på sextio tecken är sextio springs
 * och sextio DOM-noder för en effekt ingen lägger märke till.
 *
 * Tillgänglighet: uppdelningen i spans får vissa skärmläsare att läsa ord för ord.
 * Därför bär `Typography` hela texten som aria-label och spanbehållaren är aria-hidden.
 */
export function TextAvtäckning({
    text,
    enhet = 'ord',
    variant = 'body1',
    component = 'p',
    fördröjning = 0,
    avstånd = 18,
    engångs = true,
    sx
}: Props) {
    const enheter = useMemo(() => {
        if (Array.isArray(text)) return text;
        return enhet === 'ord' ? text.split(/\s+/).filter(Boolean) : [text];
    }, [text, enhet]);

    const helText = Array.isArray(text) ? text.join(' ') : text;

    const [ref, synlig] = useInView({ once: engångs, amount: 0.4 });
    const { mindreRörelse } = useRörelse();

    /**
     * Skyddsnät: text får aldrig bli permanent osynlig.
     *
     * Springsen startar på opacity 0, och om de aldrig hinner fram — fliken laddades i
     * bakgrunden så requestAnimationFrame stod stilla, ett skript dog, enheten är
     * överbelastad — hade stycket blivit borta för gott.
     *
     * Klockan startar när stycket blir synligt, inte när sidan laddas. Startade den vid
     * montering skulle allt under vikningen avslöja sig efter 2,5 sekunder, långt innan
     * läsaren scrollat dit, och rullningsavtäckningen vore borta.
     */
    const [tvingaFram, sättTvingaFram] = useState(false);
    useEffect(() => {
        if (!synlig) return undefined;
        const id = window.setTimeout(() => sättTvingaFram(true), 2500);
        return () => window.clearTimeout(id);
    }, [synlig]);

    const framme = synlig || tvingaFram;

    const spår = useTrail(enheter.length, {
        opacity: framme ? 1 : 0,
        y: framme ? 0 : avstånd,
        config: SPRING.mjuk,
        delay: mindreRörelse ? 0 : fördröjning,
        // Landar direkt även när en rörelse redan var i luften när inställningen slogs på.
        immediate: mindreRörelse || tvingaFram
    });

    return (
        <Typography ref={ref} variant={variant} component={component} aria-label={helText} sx={sx}>
            <span aria-hidden style={{ display: enhet === 'rad' ? 'block' : undefined }}>
                {spår.map((stil, i) => (
                    <AnimeradSpan
                        key={`${enheter[i]}-${i}`}
                        data-avslojad
                        style={{
                            ...stil,
                            display: enhet === 'rad' ? 'block' : 'inline-block',
                            willChange: 'transform, opacity'
                        }}
                    >
                        {enheter[i]}
                        {enhet === 'ord' && i < enheter.length - 1 ? ' ' : null}
                    </AnimeradSpan>
                ))}
            </span>
        </Typography>
    );
}
